import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TemplatesProvider } from './context/TemplatesContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Sidebar from './components/layout/Sidebar';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ToastContainer from './components/common/ToastContainer';
import { prefetchAllIdle, prefetchRoute } from './utils/routePreload';
import { installDateInputExpand } from './utils/dateInputExpand';
import { ROUTE_VERSION_CHECK_EVENT } from './services/versionApi';

// 用 prefetchRoute 來「順便」把 lazy 用到的 chunk 註冊起來，這樣 Sidebar 的 PrefetchLink
// 在 hover 時觸發的 import() 和 React.lazy 用的是同一個 promise，瀏覽器只下載一次。
const lazyRoute = (path) => lazy(() => prefetchRoute(path));

const Login = lazy(() => import('./pages/Login'));
const QuotationForm = lazyRoute('/create');
const QuotationList = lazyRoute('/quotations');
const QuotationDetail = lazyRoute('/quotations/:id');
const ShippingDocumentPage = lazyRoute('/shipping/:id');
// BIGLI-44：請款模組整條下架（PM 判定本專案不做請款），InvoiceList / InvoiceDetail
// 兩個 lazy route 連同下方的 <Route> 一起移除，/invoices 與 /invoices/:id 因此落到
// 最後的 path="*" → NotFound。頁面檔（pages/InvoiceList.js、pages/InvoiceDetail.js）
// 與後端 /api/invoices 都保留未刪；將來請款模組要上線，把這兩行 lazyRoute 與
// 下面那兩條 <Route> 加回來（連同 utils/routePreload.js 的兩條 loader）即可。
const CompanyManagement = lazyRoute('/companies');
const UserManagement = lazyRoute('/users');
const UserProfile = lazyRoute('/profile');
const NotFound = lazy(() => import('./pages/NotFound'));

const CustomerList = lazyRoute('/customers');
const CustomerDetail = lazyRoute('/customers/:id');
const CustomerForm = lazyRoute('/customers/new');

const AssetList = lazyRoute('/assets');
const AssetForm = lazyRoute('/assets/new');
const AssetDetail = lazyRoute('/assets/:id');

const ScheduleCalendar = lazyRoute('/schedule');
// BIGLI-59：LINE 新需求
const LineInquiryList = lazyRoute('/line-inquiries');
const Home = lazyRoute('/');
const CompanySettings = lazyRoute('/company-settings');

const SignaturePage = lazy(() => import('./pages/SignaturePage'));
const ShippingSignPage = lazy(() => import('./pages/ShippingSignPage'));
// BIGLI-132：報價單公開頁（LINE 連結開啟，免登入）
const QuotationPublicView = lazy(() => import('./pages/QuotationPublicView'));

const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>載入中...</p>
  </div>
);

// RENT-50：離開資產樹（/assets, /assets/new, /assets/:id, /assets/:id/edit）到非資產
// 樹的路徑時，清掉 AssetList 的 sessionStorage 篩選紀錄。Bridget 2026-06-03 補充：
// 「列表 ↔ 查看/編輯」之間要保留篩選，但從資產表離開到報價單等其他頁面後再回來，
// 預期是未篩選狀態。AssetList.js 仍負責讀寫 sessionStorage，此處只負責「離開時清除」。
const isInAssetTree = (pathname) => pathname === '/assets' || pathname.startsWith('/assets/');

const AssetFiltersLifecycle = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    if (isInAssetTree(prev) && !isInAssetTree(next)) {
      try {
        sessionStorage.removeItem('assetList:filters');
      } catch {}
    }
    prevPathRef.current = next;
  }, [location.pathname]);
  return null;
};

// BIGLI-3：公開頁（未登入即可開啟）不渲染導覽側邊欄，避免出現側欄骨架。
// /signature/:token 另外套用 signature-container 版型（原本就有的行為，不可破壞）。
// BIGLI-125：/shipping-sign/:token（出貨單客戶簽收連結）同屬公開簽署頁
// BIGLI-132：/q/:token（報價單公開連結）也是公開頁
const isSignaturePath = (pathname) => pathname.startsWith('/signature/') || pathname.startsWith('/shipping-sign/') || pathname.startsWith('/q/');
const isPublicPath = (pathname) => pathname === '/login' || isSignaturePath(pathname);

// BIGLI-11：登入後首頁（/）是「已登入但滿版」的導覽總覽頁 —— 不掛側欄、不出漢堡選單，
// 但它仍是 ProtectedRoute 底下的受保護路由，未登入開 / 一樣被導向 /login。
// 刻意獨立於 isPublicPath，才不會把「公開頁」與「滿版頁」兩種語意混在一起。
const isLandingPath = (pathname) => pathname === '/';

// /login 與 / 都要整頁鋪品牌底色，外層不能再套 .container 的 max-width 與左右 padding，
// 否則寬螢幕上底色會被夾在中間欄寬內。
const isFullBleedPath = (pathname) => pathname === '/login' || isLandingPath(pathname);

const AppLayout = ({ children }) => {
  const location = useLocation();
  const isSignaturePage = isSignaturePath(location.pathname);
  const isPublicPage = isPublicPath(location.pathname);
  const isLandingPage = isLandingPath(location.pathname);
  const hideSidebar = isPublicPage || isLandingPage;

  const mainClassName = isSignaturePage
    ? 'signature-container'
    : isFullBleedPath(location.pathname)
      ? 'full-bleed-container'
      : 'container';

  return (
    <div className={`App ${hideSidebar ? 'app-public' : 'app-with-sidebar'}`}>
      {/* 導覽在左側（Bridget 回饋後由右改左），內容區落在右側剩餘寬度。
          側欄改放在 <main> 之前，讓 Tab 焦點順序與視覺左→右一致。 */}
      {!hideSidebar && <Sidebar />}
      <main className={mainClassName}>
        {children}
      </main>
      <ToastContainer />
    </div>
  );
};

const IdlePrefetch = () => {
  useEffect(() => {
    prefetchAllIdle();
  }, []);
  return null;
};

// BIGLI-92（PM Bridget 2026-10-03「查看截圖，還是沒有修正」）：線上包早已是新標題，
// 截圖卻是換包前就開著的舊分頁。Footer 原本只在定時（5 分鐘）／切回分頁時比對版本，
// SPA 內換頁（例如點進報價單編輯）不會觸發，舊分頁會一路沿用舊畫面。
// 這裡在每次換頁時通知 Footer 立刻再比對一次，舊包就馬上跳「系統已更新」提示。
const RouteVersionPing = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // 首次掛載 Footer 自己會查，不重複打
    if (first.current) {
      first.current = false;
      return;
    }
    window.dispatchEvent(new Event(ROUTE_VERSION_CHECK_EVENT));
  }, [pathname]);
  return null;
};

// BIGLI-80：一次掛在 document 上，讓全站所有 <input type="date"> 點欄位就跳出選擇器。
const DateInputExpand = () => {
  useEffect(() => installDateInputExpand(), []);
  return null;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <TemplatesProvider>
          <ToastProvider>
            <AppLayout>
              <AssetFiltersLifecycle />
              <IdlePrefetch />
              <DateInputExpand />
              <RouteVersionPing />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Login />
                  </Suspense>
                } />

                {/* Signature page - Public access with token */}
                <Route path="/signature/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <SignaturePage />
                  </Suspense>
                } />

                {/* BIGLI-125：出貨單客戶簽收頁 - Public access with token */}
                <Route path="/shipping-sign/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ShippingSignPage />
                  </Suspense>
                } />

                {/* BIGLI-132：報價單公開頁 - LINE 連結開啟，免登入 */}
                <Route path="/q/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <QuotationPublicView />
                  </Suspense>
                } />

                {/* Protected Routes - All authenticated users */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Home />
                    </Suspense>
                  } />
                  <Route path="/profile" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserProfile />
                    </Suspense>
                  } />
                  <Route path="/quotations" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationList />
                    </Suspense>
                  } />
                  <Route path="/quotations/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationDetail />
                    </Suspense>
                  } />
                  <Route path="/shipping/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ShippingDocumentPage />
                    </Suspense>
                  } />
                  <Route path="/create" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm key="create" />
                    </Suspense>
                  } />
                  <Route path="/edit/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm key="edit" />
                    </Suspense>
                  } />
                  {/* BIGLI-44：/invoices、/invoices/:id 兩條路由已移除（PM 判定本專案
                      不做請款模組）。這裡刻意不留 <Navigate>——BIGLI-34 的訊息範本是
                      「換頁接手」所以導到 /company-settings；請款單沒有接手的頁面，
                      直接讓網址落到 path="*" 的 NotFound 才符合「畫面上完全走不到」。
                      要開回來：把 InvoiceList / InvoiceDetail 的 lazyRoute（檔案頂端）
                      與這兩條 <Route> 一起加回，頁面元件都還在 repo 裡沒有刪。 */}

                  {/* Customer Management Routes */}
                  <Route path="/customers" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerList />
                    </Suspense>
                  } />
                  <Route path="/customers/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />
                  <Route path="/customers/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerDetail />
                    </Suspense>
                  } />
                  <Route path="/customers/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />

                  {/* Asset Management Routes */}
                  <Route path="/assets" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetList />
                    </Suspense>
                  } />
                  <Route path="/assets/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />
                  <Route path="/assets/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetDetail />
                    </Suspense>
                  } />
                  <Route path="/assets/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />
                  {/* Schedule Calendar Route */}
                  <Route path="/schedule" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ScheduleCalendar />
                    </Suspense>
                  } />
                  {/* BIGLI-59：LINE 新需求（門市人員也看得到，後端依 storeScoped 限本店） */}
                  <Route path="/line-inquiries" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <LineInquiryList />
                    </Suspense>
                  } />
                </Route>

                {/* Admin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  {/* BIGLI-34：「訊息範本」頁下架。舊網址不留 404 給已把它加進書籤的管理員，
                      直接導到接手它的 /company-settings（公司 LOGO／公司簽名／公司資訊／
                      銀行資訊／AI 設定都在那頁）。replace 是為了不讓「上一頁」彈回這條
                      重導、把使用者困在無限循環裡。
                      放在本 ProtectedRoute 內：門市人員打這個網址仍會先被角色守門擋下並
                      導回導覽總覽頁，行為與下架前一致。 */}
                  <Route path="/message-templates" element={<Navigate to="/company-settings" replace />} />
                  <Route path="/company-settings" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanySettings />
                    </Suspense>
                  } />
                </Route>

                {/* Superadmin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['superadmin']} />}>
                  <Route path="/companies" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanyManagement />
                    </Suspense>
                  } />
                  <Route path="/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                </Route>

                {/* User Management - Accessible by superadmin and admin */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  <Route path="/companies/:companyId/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                </Route>

                {/* Not Found */}
                <Route path="*" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
            </AppLayout>
          </ToastProvider>
        </TemplatesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
